import React, { useEffect, useState, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, resolveAvatarUrl } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Upload, Trash2, ExternalLink } from "lucide-react";

const TEAMS_COLOR = "#5B5FC7";

export default function EmployeeDrawer({ employee, employees, open, onClose, onSaved, onDeleted, scenarioId, readOnly = false }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/custom-fields").then((r) => setCustomFields(r.data?.fields || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (employee) {
      setForm({
        name: employee.name || "",
        designation: employee.designation || "",
        department: employee.department || "",
        email: employee.email || "",
        manager_id: employee.manager_id || "none",
        photo_url: employee.photo_url || "",
        custom_data: { ...(employee.custom_data || {}) },
      });
    }
  }, [employee]);

  if (!employee || !form) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, manager_id: form.manager_id === "none" ? null : form.manager_id };
      if (scenarioId) {
        await api.put(`/scenarios/${scenarioId}/override`, { employee_id: employee.id, changes: payload });
        toast.success("Saved to scenario");
      } else {
        const { data } = await api.put(`/employees/${employee.id}`, payload);
        toast.success("Employee updated");
        onSaved?.(data);
        return;
      }
      onSaved?.({ ...employee, ...payload });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (scenarioId) {
      if (!window.confirm(`Mark ${employee.name} as removed in this scenario? Live data is untouched until merge.`)) return;
      try {
        if (employee.id.startsWith("scen-")) {
          // Drop a scenario-only added employee
          await api.delete(`/scenarios/${scenarioId}/add/${employee.id}`);
        } else {
          await api.post(`/scenarios/${scenarioId}/remove/${employee.id}`);
        }
        toast.success("Marked as removed in scenario");
        onDeleted?.(employee.id);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Update failed");
      }
      return;
    }
    if (!window.confirm(`Delete ${employee.name}?`)) return;
    try {
      await api.delete(`/employees/${employee.id}`);
      toast.success("Employee deleted");
      onDeleted?.(employee.id);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/${employee.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar uploaded");
      onSaved?.({ ...employee, ...form });
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const openTeamsChat = async () => {
    if (!employee.email) {
      toast.error("No email on file");
      return;
    }
    try {
      const { data } = await api.get(`/teams/deeplink?email=${encodeURIComponent(employee.email)}`);
      window.open(data.url, "_blank", "noopener");
    } catch {
      toast.error("Could not open Teams");
    }
  };

  const avatar = resolveAvatarUrl({ ...employee, ...form });

  const status = employee.sync_status || (employee.source === "microsoft" ? "synced" : "local");
  const statusLabels = {
    synced: { label: "Synced from Microsoft", color: "bg-[#0A7029]" },
    pending: { label: "Local edit pending", color: "bg-[#D83B01]" },
    pending_push: { label: "Awaiting push to Microsoft", color: "bg-[#D83B01]" },
    local: { label: "Local only", color: "bg-neutral-700" },
  };
  const s = statusLabels[status] || statusLabels.local;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto rounded-none border-l border-neutral-200 bg-white p-0 sm:max-w-md"
        data-testid="employee-drawer"
      >
        <div className="border-b border-neutral-200 px-6 py-5">
          <SheetHeader>
            <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              Employee Detail
            </p>
            <SheetTitle className="mt-1 text-xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
              {employee.name}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-3 flex items-center gap-2">
            <span className={`inline-block h-1.5 w-1.5 ${s.color}`} />
            <span className="text-[11px] uppercase tracking-wider text-neutral-600" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              {s.label}
            </span>
            {employee.source === "microsoft" && (
              <Badge variant="outline" className="rounded-none border-neutral-300 text-[10px]">
                MS · {employee.ms_id}
              </Badge>
            )}
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="flex items-center gap-4">
            {avatar ? (
              <img src={avatar} alt="" className="h-20 w-20 object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center bg-[#002FA7] text-lg text-white">
                {(form.name || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="avatar-file-input" />
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-neutral-300"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-avatar-button"
              >
                <Upload size={14} className="mr-2" />
                Upload photo
              </Button>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-none border-neutral-300"
              data-testid="employee-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Designation</Label>
            <Input
              value={form.designation}
              onChange={(e) => setForm({ ...form, designation: e.target.value })}
              className="rounded-none border-neutral-300"
              data-testid="employee-designation-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Department</Label>
            <Input
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="rounded-none border-neutral-300"
              data-testid="employee-department-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Email</Label>
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-none border-neutral-300"
              data-testid="employee-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Reports to</Label>
            <Select
              value={form.manager_id || "none"}
              onValueChange={(v) => setForm({ ...form, manager_id: v })}
            >
              <SelectTrigger className="rounded-none border-neutral-300" data-testid="manager-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (Root) —</SelectItem>
                {employees
                  .filter((e) => e.id !== employee.id)
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} — {e.designation || "—"}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {customFields.length > 0 && (
            <div className="space-y-3 border-t border-neutral-200 pt-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                Custom fields
              </p>
              {customFields.map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wider text-neutral-500">{f.label}</Label>
                  {f.type === "select" ? (
                    <Select
                      value={form.custom_data?.[f.key] || ""}
                      onValueChange={(v) => setForm({ ...form, custom_data: { ...form.custom_data, [f.key]: v } })}
                    >
                      <SelectTrigger className="rounded-none border-neutral-300" data-testid={`custom-${f.key}-input`}>
                        <SelectValue placeholder="—"/>
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={form.custom_data?.[f.key] || ""}
                      onChange={(e) => setForm({ ...form, custom_data: { ...form.custom_data, [f.key]: e.target.value } })}
                      className="rounded-none border-neutral-300"
                      data-testid={`custom-${f.key}-input`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || readOnly}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
              data-testid="save-employee-button"
            >
              {saving ? "Saving…" : scenarioId ? "Save to scenario" : "Save changes"}
            </Button>
            <Button
              variant="outline"
              onClick={openTeamsChat}
              className="rounded-none border text-white hover:opacity-90"
              style={{ background: TEAMS_COLOR, borderColor: TEAMS_COLOR }}
              data-testid="chat-on-teams-button"
            >
              <MessageSquare size={14} className="mr-2" />
              Chat on Teams
              <ExternalLink size={12} className="ml-2" />
            </Button>
            {!scenarioId && (
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={readOnly}
                className="rounded-none text-[#C50F1F] hover:bg-red-50 hover:text-[#C50F1F]"
                data-testid="delete-employee-button"
              >
                <Trash2 size={14} className="mr-2" />
                Delete
              </Button>
            )}
            {scenarioId && (
              <Button
                variant="ghost"
                onClick={handleDelete}
                className="rounded-none text-[#C50F1F] hover:bg-red-50 hover:text-[#C50F1F]"
                data-testid="scenario-remove-employee-button"
              >
                <Trash2 size={14} className="mr-2"/>
                Remove (scenario)
              </Button>
            )}
            {readOnly && (
              <p className="text-[11px] uppercase tracking-wider text-[#D83B01]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                Read-only · time-travel mode
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
